import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
 

// ------------------- DATA ARRAYS -------------------
const servicesData = [
  {
    icon: 'fa-solid fa-cloud-arrow-up',
    title: 'Microsoft Cloud Solutions',
    desc: 'Empower your enterprise with Azure infrastructure, Power Platform automation, and Azure OpenAI integration.',
    link: '/microsoft-cloud',
  },
  {
    icon: 'fa-brands fa-aws',
    title: 'AWS Cloud Solutions',
    desc: 'Scalable serverless microservices, Amazon Bedrock generative AI, and resilient Well-Architected cloud frameworks.',
    link: '/aws-cloud',
  },
  {
    icon: 'fa-solid fa-shield-halved',
    title: 'Cybersecurity & Governance',
    desc: 'Enforce Zero-Trust security postures, threat monitoring, and strict regulatory compliance across systems.',
    link: '/cybersecurity',
  },
  {
    icon: 'fa-solid fa-code',
    title: 'Custom Software Engineering',
    desc: 'Modern cloud-native web/mobile application architectures designed for ultra-high concurrency and performance.',
    link: '/custom-software',
  },
];

const digitalTransformationData = [
  {
    icon: 'fa-solid fa-brain',
    title: 'Enterprise AI & Copilot Integration',
    desc: 'Deploy custom LLM agents, dynamic RAG pipelines, and automated intelligence across business units.',
    link: '/digital-transformation/ai',
  },
  {
    icon: 'fa-solid fa-arrows-spin',
    title: 'Legacy Application Modernization',
    desc: 'Refactor monoliths into microservices using automated CI/CD and containerization technologies.',
    link: '/digital-transformation/modernization',
  },
  {
    icon: 'fa-solid fa-chart-line-down',
    title: 'Cloud FinOps & Cost Optimization',
    desc: 'Eliminate waste, govern cloud spend, and optimize reserved instance allocations across AWS and Azure.',
    link: '/digital-transformation/finops',
  },
  {
    icon: 'fa-solid fa-network-wired',
    title: 'Workflow & Process Automation',
    desc: 'Automate high-volume administrative workflows with intelligent low-code enterprise applications.',
    link: '/digital-transformation/automation',
  },
];

const technologyStackData = [
  {
    icon: 'fa-solid fa-cloud',
    category: 'Cloud Infrastructure',
    tools: ['AWS', 'Microsoft Azure', 'Kubernetes (AKS/EKS)', 'Terraform', 'Docker'],
    link: '/technology/cloud',
  },
  {
    icon: 'fa-solid fa-robot',
    category: 'Artificial Intelligence',
    tools: ['Azure OpenAI', 'AWS Bedrock', 'PyTorch', 'TensorFlow', 'LangChain'],
    link: '/technology/ai',
  },
  {
    icon: 'fa-solid fa-database',
    category: 'Data Engineering',
    tools: ['Microsoft Fabric', 'Amazon Redshift', 'Snowflake', 'Apache Kafka', 'PostgreSQL'],
    link: '/technology/data',
  },
  {
    icon: 'fa-solid fa-layer-group',
    category: 'Modern Web & Mobile',
    tools: ['React.js', 'Node.js', 'TypeScript', 'Python', 'Flutter / React Native'],
    link: '/technology/web-mobile',
  },
];

const industriesData = [
  {
    icon: 'fa-solid fa-industry',
    title: 'Smart Manufacturing',
    desc: 'Industry 4.0 IIoT edge data capture, computer vision quality inspection, and predictive maintenance.',
    link: '/manufacturing-solutions',
  },
  {
    icon: 'fa-solid fa-notes-medical',
    title: 'Healthcare & Biotech',
    desc: 'HIPAA/FDA compliant SaMD, FHIR/HL7 EHR interoperability, and genomic data processing pipelines.',
    link: '/healthcare-biotech-solutions',
  },
  {
    icon: 'fa-solid fa-car-tunnel',
    title: 'Automotive & Mobility',
    desc: 'Software-Defined Vehicles (SDV), AUTOSAR embedded firmware, and connected vehicle clouds.',
    link: '/automotive-solutions',
  },
  {
    icon: 'fa-solid fa-tv',
    title: 'Media & Entertainment',
    desc: 'High-concurrency OTT video streaming, multi-DRM content security, and turn-key FAST channels.',
    link: '/media-entertainment-solutions',
  },
];

// ------------------- ANIMATION VARIANTS -------------------
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const Home = () => {
  return (
    <div className="home-wrapper text-white overflow-hidden bg-dark-main">
      {/* 1. HERO SECTION */}
     <section className="hero-section text-white d-flex flex-column justify-content-center align-items-center position-relative py-5 overflow-hidden">
  {/* Background Video */}
  <video
    autoPlay
    loop
    muted
    playsInline
    className="position-absolute top-0 start-0 w-100 h-100 object-fit-cover"
    style={{ zIndex: 0 }}
  >
    <source src="img/home.mp4" type="video/mp4" />
    Your browser does not support the video tag.
  </video>

  {/* Dark Overlay (Enhances text readability over video) */}
  <div 
    className="position-absolute top-0 start-0 w-100 h-100 bg-dark opacity-50" 
    style={{ zIndex: 1 }}
  />

  {/* Foreground Hero Content */}
  <div className="container text-center hero-content my-5 position-relative" style={{ zIndex: 2 }}>
    

    {/* Main Heading */}
    <h1 className="display-4 fw-extrabold hero-title mb-4">
      Continue your quest for <br />
      <span className="gradient-text-cyan">
        limitless possibilities with our disruptive
      </span>{' '}
      <br />
      <span className="gradient-text-orange">innovation</span>
    </h1>

    <p className="fs-5 text-light opacity-75 mb-5">
      Your Partners in Building the Exceptional
    </p>

    {/* CTA Button */}
    <Link to="/contact" className="btn gradient-btn btn-lg px-5 py-3 rounded-pill fw-semibold">
      Let's Talk &rarr;
    </Link>
  </div>
</section>

      {/* 2. SERVICES SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <span className="badge rounded-pill bg-outline-cyan px-3 py-2 mb-2">Our Capabilities</span>
            <h2 className="fw-bold">
              Core <span className="gradient-text-cyan">Services</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
          >
            {servicesData.map((item, index) => (
              <div key={index} className="col-md-6 col-lg-3">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ y: -8, transition: { duration: 0.2 } }}
                  className="service-card p-4 rounded-4 h-100 border border-secondary border-opacity-25 bg-glass d-flex flex-column justify-content-between"
                >
                  <div>
                    <div className="icon-box mb-3 d-flex align-items-center justify-content-center floating-icon">
                      <i className={`${item.icon} fs-3 text-cyan`}></i>
                    </div>
                    <h5 className="fw-bold mb-2">{item.title}</h5>
                    <p className="text-light opacity-75 small mb-4">{item.desc}</p>
                  </div>
                  <Link to={item.link} className="btn btn-sm btn-outline-cyan rounded-pill align-self-start">
                    Explore Service &rarr;
                  </Link>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 3. DIGITAL TRANSFORMATION SECTION */}
      <section className="py-5 bg-dark-overlay">
        <div className="container">
          <div className="text-center mb-5">
            <span className="badge rounded-pill bg-outline-cyan px-3 py-2 mb-2">Modernize Your Business</span>
            <h2 className="fw-bold">
              Digital <span className="gradient-text-orange">Transformation</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
          >
            {digitalTransformationData.map((item, index) => (
              <div key={index} className="col-md-6 col-lg-3">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ y: -8, transition: { duration: 0.2 } }}
                  className="service-card p-4 rounded-4 h-100 border border-secondary border-opacity-25 bg-glass d-flex flex-column justify-content-between"
                >
                  <div>
                    <div className="icon-box mb-3 d-flex align-items-center justify-content-center floating-icon">
                      <i className={`${item.icon} fs-3 text-orange`}></i>
                    </div>
                    <h5 className="fw-bold mb-2">{item.title}</h5>
                    <p className="text-light opacity-75 small mb-4">{item.desc}</p>
                  </div>
                  <Link to={item.link} className="btn btn-sm btn-outline-orange rounded-pill align-self-start">
                    Learn More &rarr;
                  </Link>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 4. TECHNOLOGY STACK SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <span className="badge rounded-pill bg-outline-cyan px-3 py-2 mb-2">Technical Foundations</span>
            <h2 className="fw-bold">
              Technology <span className="gradient-text-cyan">Ecosystem</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
          >
            {technologyStackData.map((tech, index) => (
              <div key={index} className="col-md-6 col-lg-3">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ y: -8, transition: { duration: 0.2 } }}
                  className="service-card p-4 rounded-4 h-100 border border-secondary border-opacity-25 bg-glass d-flex flex-column justify-content-between"
                >
                  <div>
                    <div className="icon-box mb-3 d-flex align-items-center justify-content-center floating-icon">
                      <i className={`${tech.icon} fs-3 text-cyan`}></i>
                    </div>
                    <h5 className="fw-bold mb-3">{tech.category}</h5>
                    <div className="d-flex flex-wrap gap-2 mb-4">
                      {tech.tools.map((tool, i) => (
                        <span key={i} className="badge bg-secondary bg-opacity-25 text-light border border-secondary border-opacity-50">
                          {tool}
                        </span>
                      ))}
                    </div>
                  </div>
                  <Link to={tech.link} className="btn btn-sm btn-outline-cyan rounded-pill align-self-start">
                    View Stack &rarr;
                  </Link>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 5. INDUSTRIES SECTION */}
      <section className="py-5 bg-dark-overlay">
        <div className="container">
          <div className="text-center mb-5">
            <span className="badge rounded-pill bg-outline-cyan px-3 py-2 mb-2">Vertical Expertise</span>
            <h2 className="fw-bold">
              Industries <span className="gradient-text-orange">We Serve</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
          >
            {industriesData.map((item, index) => (
              <div key={index} className="col-md-6 col-lg-3">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ y: -8, transition: { duration: 0.2 } }}
                  className="service-card p-4 rounded-4 h-100 border border-secondary border-opacity-25 bg-glass d-flex flex-column justify-content-between"
                >
                  <div>
                    <div className="icon-box mb-3 d-flex align-items-center justify-content-center floating-icon">
                      <i className={`${item.icon} fs-3 text-orange`}></i>
                    </div>
                    <h5 className="fw-bold mb-2">{item.title}</h5>
                    <p className="text-light opacity-75 small mb-4">{item.desc}</p>
                  </div>
                  <Link to={item.link} className="btn btn-sm btn-outline-orange rounded-pill align-self-start">
                    Industry Solutions &rarr;
                  </Link>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 6. CALL TO ACTION BANNER */}
      <section className="py-5 my-4">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="p-5 rounded-5 cta-banner text-center position-relative overflow-hidden"
          >
            <h2 className="fw-bold mb-3">Ready to Scale with Lidlbritain?</h2>
            <p className="lead opacity-90 mx-auto mb-4 max-width-800">
              Transform your digital operations with our certified enterprise cloud architects and AI engineers.
            </p>
            <motion.div whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
              <Link to="/contact" className="btn gradient-btn btn-lg px-5 py-3 rounded-pill fw-semibold">
                Get Started Today &rarr;
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;